// Guest "drop link" upload client. Same S3 multipart mechanics as
// uploadService.js, but authenticated by a link token (+ optional passcode)
// instead of a Firebase login, and the backend assigns every key under the
// item's dropbox/<token>/ prefix. No Firebase SDK needed → works for guests.
//
// Resilience + smarts live in utils/multipart.js (retry/backoff, resume from
// the last finished part). This module adds drop-specific concerns: the API
// adapter, a per-file checkpoint store, a "done set" + folder manifest so a
// re-dropped folder skips already-finished files and reports new/missing/
// changed files.
import { runMultipart, withRetry, PART_SIZE } from '../utils/multipart';

const API = process.env.REACT_APP_UPLOAD_API || '';
const SESSION_PREFIX = 'kit_drop_session:';
const DONE_PREFIX = 'kit_drop_done:';
const MANIFEST_PREFIX = 'kit_drop_manifest:';

export const isDropConfigured = () => !!API;

async function postJson(path, body, signal) {
  const res = await fetch(`${API}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) {
    let msg = `${res.status}`;
    try {
      const j = await res.json();
      msg = j.error || msg;
    } catch (_e) {
      /* ignore */
    }
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

// Fetch link info (item name, passcode requirement, validity, prefilled guest).
export async function getDropInfo(token) {
  const res = await fetch(`${API}/drop/${encodeURIComponent(token)}`);
  if (!res.ok) {
    const err = new Error(`link ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

// ---- localStorage helpers ----------------------------------------------
const safeGet = (k) => {
  try {
    const raw = localStorage.getItem(k);
    return raw ? JSON.parse(raw) : null;
  } catch (_e) {
    return null;
  }
};
const safeSet = (k, v) => {
  try {
    localStorage.setItem(k, JSON.stringify(v));
  } catch (_e) {
    /* storage full/blocked */
  }
};
const safeDel = (k) => {
  try {
    localStorage.removeItem(k);
  } catch (_e) {
    /* ignore */
  }
};

// A file is identified across reloads by relPath+size+lastModified.
export const fingerprintOf = (file, relPath) => `${relPath || file.name}|${file.size}|${file.lastModified || 0}`;
const sessionKey = (token, fp) => `${SESSION_PREFIX}${token}:${fp}`;

function makeSession(token, fp) {
  return {
    load: () => safeGet(sessionKey(token, fp)),
    save: (s) => safeSet(sessionKey(token, fp), s),
    clear: () => safeDel(sessionKey(token, fp)),
  };
}

// Interrupted uploads still on disk for this link (for a "resume" hint).
export function listResumableSessions(token) {
  const out = [];
  try {
    const prefix = `${SESSION_PREFIX}${token}:`;
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (k && k.startsWith(prefix)) {
        const v = safeGet(k);
        if (v) out.push(v);
      }
    }
  } catch (_e) {
    /* ignore */
  }
  return out;
}

// ---- "Done set": files fully uploaded + recorded under this link --------
const doneKey = (token) => `${DONE_PREFIX}${token}`;
export const getDoneMap = (token) => safeGet(doneKey(token)) || {};
export function markDropFileDone(token, fp, meta) {
  const map = getDoneMap(token);
  map[fp] = { ...meta, at: Date.now() };
  safeSet(doneKey(token), map);
}
export const isDropFileDone = (token, fp) => !!getDoneMap(token)[fp];
export const getDropFileDone = (token, fp) => getDoneMap(token)[fp] || null;

// ---- Folder manifest: metadata snapshot for change detection ------------
const manifestKey = (token) => `${MANIFEST_PREFIX}${token}`;

// Build a manifest entry for one folder group.
export function manifestForGroup(group) {
  return {
    folderName: group.folderName,
    totalSize: group.totalSize,
    fileCount: group.files.length,
    files: group.files.map((p) => ({
      inner: p.inner,
      size: p.file.size,
      lastModified: p.file.lastModified || 0,
    })),
    updatedAt: Date.now(),
  };
}

export function saveFolderManifest(token, group) {
  const all = safeGet(manifestKey(token)) || {};
  all[group.folderName] = manifestForGroup(group);
  safeSet(manifestKey(token), all);
}

// Deterministic relative path (and thus S3 key + fingerprint) for a folder
// part, so a re-dropped folder maps to the same upload and can resume.
export const groupPartRelPath = (group, inner) => `${group.folderName}/${inner}`;

// Compare a freshly-dropped folder group against what we know (manifest +
// done-set). Returns counts + lists so the UI can report recognition.
export function inspectGroup(token, group) {
  const all = safeGet(manifestKey(token)) || {};
  const prior = all[group.folderName] || null;
  const priorByInner = new Map((prior?.files || []).map((f) => [f.inner, f]));
  const seen = new Set();
  let done = 0;
  let changed = 0;
  let fresh = 0;
  for (const part of group.files) {
    seen.add(part.inner);
    const fp = fingerprintOf(part.file, groupPartRelPath(group, part.inner));
    const wasDone = isDropFileDone(token, fp);
    const known = priorByInner.get(part.inner);
    if (wasDone) done += 1;
    else if (known && (known.size !== part.file.size || known.lastModified !== (part.file.lastModified || 0))) changed += 1;
    else if (!known) fresh += 1;
  }
  const missing = (prior?.files || []).filter((f) => !seen.has(f.inner)).map((f) => f.inner);
  return {
    recognized: !!prior,
    folderName: group.folderName,
    total: group.files.length,
    done,
    changed,
    fresh,
    missing,
    toUpload: group.files.length - done,
  };
}

// ---- Drop API adapter ----------------------------------------------------
const enc = encodeURIComponent;
function dropAdapter(token, relPath, contentType, file, passcode) {
  return {
    create: () =>
      postJson(`/drop/${enc(token)}/create`, {
        relPath,
        contentType: contentType || file.type || 'application/octet-stream',
        passcode,
      }),
    sign: (uploadId, key, partNumber) => postJson(`/drop/${enc(token)}/sign`, { key, uploadId, partNumber, passcode }),
    complete: (uploadId, key, parts) => postJson(`/drop/${enc(token)}/complete`, { key, uploadId, parts, passcode }),
    listParts: (uploadId, key) => postJson(`/drop/${enc(token)}/parts`, { key, uploadId, passcode }),
  };
}

// Uploads one file through a drop link, resuming from any checkpoint.
export async function uploadFileToDrop(
  file,
  { token, relPath, passcode, contentType, onProgress, onState, onRecognized, monitor, signal }
) {
  if (!isDropConfigured()) throw new Error('Uploads are not configured.');
  const fp = fingerprintOf(file, relPath);
  const adapter = dropAdapter(token, relPath, contentType, file, passcode);
  const { key } = await runMultipart(file, {
    ...adapter,
    session: makeSession(token, fp),
    onProgress,
    onState,
    onRecognized,
    monitor,
    signal,
    fileId: fp,
    partSize: PART_SIZE,
  });
  return { key };
}

// Abort an in-flight multipart upload and forget its checkpoint.
export async function abortDrop(file, { token, relPath, passcode }) {
  const fp = fingerprintOf(file, relPath);
  const session = makeSession(token, fp).load();
  safeDel(sessionKey(token, fp));
  if (session?.uploadId) {
    await postJson(`/drop/${enc(token)}/abort`, { key: session.key, uploadId: session.uploadId, passcode }).catch(() => {});
  }
}

// Record a finished upload (single file or folder group) as an item asset.
// `clientInfo` carries the guest's browser fingerprint + optional GPS; the
// backend merges it with the server-observed IP/geo for provenance records.
export async function recordDropAsset({ token, asset, contributor, passcode, clientInfo }) {
  return withRetry(() => postJson(`/drop/${enc(token)}/record`, { asset, contributor, passcode, clientInfo }));
}

// Best-effort live progress heartbeat. Guests can't write Firestore (no
// account), so the browser posts progress here and the backend mirrors it into
// items/{itemId}/uploads/{id} for the owner's "uploading now" view. Fire-and-
// forget: never let a failed beat disturb the actual upload.
export function reportDropProgress({ token, passcode, ...payload }) {
  if (!isDropConfigured()) return Promise.resolve();
  return postJson(`/drop/${enc(token)}/progress`, { ...payload, passcode }).catch(() => {});
}
